import type {
  AlertSeverity,
  CreateNotificationChannelRequest,
  NotificationChannelConfig,
  NotificationChannelKind,
  NotificationChannelSecret,
  NotificationChannelView,
} from '@occ/contracts';
import { useCan } from '@refinedev/core';
import { Hash, Mail, MessageCircle, Pencil, Plus, Send, Trash2, Webhook } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { describeHttpError } from '../../api/http';
import { ErrorNotice } from '../../components/ErrorNotice';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  Loading,
  Modal,
  Select,
  Toasts,
  useToasts,
} from '../../components/ui';
import {
  createNotificationChannel,
  deleteNotificationChannel,
  listNotificationChannels,
  testNotificationChannel,
  updateNotificationChannel,
} from '../../data/notification-channels';

/**
 * Canales de notificación (M12/M15 §3).
 *
 * El backend ya evita `.env` para lo que rota —webhook de Slack, token de
 * WhatsApp, ahora el de Discord—: vive cifrado en el canal, editable por esta
 * pantalla sin redeploy. Lo que faltaba no era el modelo, era esta pantalla.
 *
 * Crear o editar un canal no es algo que un nodo ejecute, así que vive aquí y
 * no en el catálogo de acciones — mismo criterio que plugins y reglas de
 * alerta (ver el comentario de `notifications.controller.ts`).
 */

const KIND_LABEL: Record<NotificationChannelKind, string> = {
  slack: 'Slack',
  whatsapp: 'WhatsApp',
  email: 'Correo',
  discord: 'Discord',
};

const KIND_ICON: Record<NotificationChannelKind, typeof Hash> = {
  slack: Hash,
  whatsapp: MessageCircle,
  email: Mail,
  discord: Webhook,
};

const SECRET_LABEL: Record<NotificationChannelKind, string> = {
  slack: 'URL del webhook',
  discord: 'URL del webhook',
  whatsapp: 'Token de acceso',
  email: 'Contraseña propia del relay',
};

type ChannelForm = {
  id: string | null;
  kind: NotificationChannelKind;
  name: string;
  enabled: boolean;
  minSeverity: AlertSeverity;
  hasOwnSecret: boolean;

  slackChannel: string;
  whatsappPhoneNumberId: string;
  whatsappRecipients: string;
  whatsappTemplateName: string;
  whatsappTemplateLanguage: string;
  emailRecipients: string;
  emailFrom: string;
  emailSubjectPrefix: string;
  discordLabel: string;

  /** Vacío = no tocar la credencial existente (o usar la de la instalación en un canal nuevo). */
  secretValue: string;
};

const formOf = (channel?: NotificationChannelView): ChannelForm => {
  const config = channel?.config;
  return {
    id: channel?.id ?? null,
    kind: channel?.kind ?? 'slack',
    name: channel?.name ?? '',
    enabled: channel?.enabled ?? true,
    minSeverity: channel?.minSeverity ?? 'warning',
    hasOwnSecret: channel?.hasOwnSecret ?? false,

    slackChannel: config?.kind === 'slack' ? (config.channel ?? '') : '',
    whatsappPhoneNumberId: config?.kind === 'whatsapp' ? (config.phoneNumberId ?? '') : '',
    whatsappRecipients: config?.kind === 'whatsapp' ? config.recipients.join(', ') : '',
    whatsappTemplateName: config?.kind === 'whatsapp' ? config.templateName : '',
    whatsappTemplateLanguage: config?.kind === 'whatsapp' ? config.templateLanguage : 'es',
    emailRecipients: config?.kind === 'email' ? config.recipients.join(', ') : '',
    emailFrom: config?.kind === 'email' ? (config.from ?? '') : '',
    emailSubjectPrefix: config?.kind === 'email' ? (config.subjectPrefix ?? '') : '',
    discordLabel: config?.kind === 'discord' ? (config.label ?? '') : '',

    secretValue: '',
  };
};

const splitList = (value: string): string[] =>
  value
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);

const buildConfig = (form: ChannelForm): NotificationChannelConfig => {
  switch (form.kind) {
    case 'slack':
      return { kind: 'slack', channel: form.slackChannel.trim() || undefined };
    case 'whatsapp':
      return {
        kind: 'whatsapp',
        phoneNumberId: form.whatsappPhoneNumberId.trim() || undefined,
        recipients: splitList(form.whatsappRecipients),
        templateName: form.whatsappTemplateName.trim(),
        templateLanguage: form.whatsappTemplateLanguage.trim() || 'es',
      };
    case 'email':
      return {
        kind: 'email',
        recipients: splitList(form.emailRecipients),
        from: form.emailFrom.trim() || undefined,
        subjectPrefix: form.emailSubjectPrefix.trim() || undefined,
      };
    case 'discord':
      return { kind: 'discord', label: form.discordLabel.trim() || undefined };
  }
};

/** `undefined` deja la credencial como está — nunca se manda un valor censurado de vuelta. */
const buildSecret = (form: ChannelForm): NotificationChannelSecret | undefined => {
  const value = form.secretValue.trim();
  if (!value) return undefined;
  switch (form.kind) {
    case 'slack':
    case 'discord':
      return { kind: form.kind, webhookUrl: value };
    case 'whatsapp':
      return { kind: 'whatsapp', accessToken: value };
    case 'email':
      return { kind: 'email', password: value };
  }
};

const canSubmit = (form: ChannelForm): boolean => {
  if (form.name.trim() === '') return false;
  if (form.kind === 'whatsapp') {
    return splitList(form.whatsappRecipients).length > 0 && form.whatsappTemplateName.trim() !== '';
  }
  if (form.kind === 'email') {
    return splitList(form.emailRecipients).length > 0;
  }
  // Slack y Discord: el destino lo decide el webhook. Uno nuevo lo necesita si
  // no tiene ya credencial propia; uno existente puede guardarse sin tocarla.
  return form.id !== null || form.secretValue.trim() !== '';
};

export function NotificationChannelsPage() {
  const { data: canEdit } = useCan({ resource: 'notification-channels', action: 'edit' });

  const [channels, setChannels] = useState<NotificationChannelView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState<ChannelForm | null>(null);
  const [deleting, setDeleting] = useState<NotificationChannelView | null>(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);

  const toasts = useToasts();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setChannels(await listNotificationChannels());
      setError(null);
    } catch (cause) {
      setError(cause);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async () => {
    if (!form) return;
    setBusy(true);
    try {
      const base = {
        name: form.name.trim(),
        enabled: form.enabled,
        minSeverity: form.minSeverity,
        config: buildConfig(form),
        secret: buildSecret(form),
      };
      if (form.id) {
        await updateNotificationChannel(form.id, base);
      } else {
        await createNotificationChannel(base as CreateNotificationChannelRequest);
      }
      setForm(null);
      toasts.ok(form.id ? 'Canal actualizado.' : 'Canal creado.');
      await load();
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await deleteNotificationChannel(deleting.id);
      setDeleting(null);
      toasts.ok('Canal borrado.');
      await load();
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    } finally {
      setBusy(false);
    }
  };

  const test = async (channel: NotificationChannelView) => {
    setTesting(channel.id);
    try {
      const result = await testNotificationChannel(channel.id);
      if (result.status === 'sent') {
        toasts.ok(`Prueba enviada por ${KIND_LABEL[channel.kind]}.`);
      } else {
        toasts.fail(result.error);
      }
      await load();
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    } finally {
      setTesting(null);
    }
  };

  return (
    <section className="grid gap-4">
      <Toasts toasts={toasts.toasts} />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Canales de notificación</h1>
          <p className="text-sm opacity-60">
            Slack, WhatsApp, correo y Discord. Las reglas de alerta y las tareas programadas avisan
            por aquí; el interruptor de cada nodo elige entre estos mismos canales.
          </p>
        </div>
        <Button
          variant="primary"
          disabled={!canEdit?.can}
          title={canEdit?.can ? undefined : canEdit?.reason}
          onClick={() => setForm(formOf())}
        >
          <Plus className="size-4" />
          Nuevo canal
        </Button>
      </div>

      {!canEdit?.can && (
        <Alert>{canEdit?.reason ?? 'Solo un administrador del tenant administra canales.'}</Alert>
      )}

      {error !== null && <ErrorNotice error={error} what="Cargar los canales de notificación" />}

      {loading && channels.length === 0 ? (
        <Loading label="Cargando canales…" />
      ) : channels.length === 0 ? (
        <Card>
          <EmptyState title="Ningún canal todavía">
            Un canal es un destino de avisos —un webhook de Slack o Discord, un número de WhatsApp,
            una lista de correo— que una regla de alerta o una tarea programada puede usar.
          </EmptyState>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {channels.map((channel) => {
            const Icon = KIND_ICON[channel.kind];
            return (
              <Card key={channel.id} className="flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h2 className="inline-flex items-center gap-1.5 font-semibold tracking-tight">
                      <Icon className="size-4 opacity-70" />
                      {channel.name}
                    </h2>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      <Badge>{KIND_LABEL[channel.kind]}</Badge>
                      <Badge tone={channel.enabled ? 'ok' : 'neutral'}>
                        {channel.enabled ? 'habilitado' : 'deshabilitado'}
                      </Badge>
                      <Badge>≥ {channel.minSeverity}</Badge>
                      {channel.hasOwnSecret ? (
                        <Badge>credencial propia</Badge>
                      ) : (
                        <Badge>credencial de la instalación</Badge>
                      )}
                    </div>
                  </div>
                </div>

                <p className="mt-2 text-xs opacity-60">
                  {channel.lastDeliveryAt
                    ? `Última entrega: ${channel.lastDeliveryStatus === 'sent' ? 'enviada' : 'fallida'}`
                    : 'Sin envíos todavía.'}
                </p>
                {channel.lastDeliveryStatus === 'failed' && channel.lastDeliveryError && (
                  <p className="mt-1 line-clamp-2 text-xs text-destructive">
                    {channel.lastDeliveryError}
                  </p>
                )}

                <div className="mt-3 flex justify-end gap-1 border-t border-black/5 pt-2 dark:border-white/5">
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Enviar prueba"
                    aria-label="Enviar prueba"
                    disabled={testing === channel.id}
                    onClick={() => void test(channel)}
                  >
                    <Send className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Editar"
                    aria-label="Editar"
                    disabled={!canEdit?.can}
                    onClick={() => setForm(formOf(channel))}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Borrar"
                    aria-label="Borrar"
                    disabled={!canEdit?.can}
                    className="hover:text-destructive"
                    onClick={() => setDeleting(channel)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {form && (
        <ChannelFormModal
          form={form}
          busy={busy}
          onChange={setForm}
          onCancel={() => setForm(null)}
          onSubmit={() => void submit()}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Borrar canal"
        confirmLabel="Borrar"
        confirmText={deleting?.name}
        busy={busy}
        onCancel={() => setDeleting(null)}
        onConfirm={() => void remove()}
      >
        <p>
          Se borra <strong>{deleting?.name}</strong>. Las reglas y tareas que lo usaban dejan de
          avisar por aquí; el historial de entregas ya enviadas no se toca.
        </p>
      </ConfirmDialog>
    </section>
  );
}

function ChannelFormModal({
  form,
  busy,
  onChange,
  onCancel,
  onSubmit,
}: {
  form: ChannelForm;
  busy: boolean;
  onChange: (form: ChannelForm) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const patch = (fields: Partial<ChannelForm>) => onChange({ ...form, ...fields });
  const isNew = form.id === null;

  return (
    <Modal
      open
      title={isNew ? 'Nuevo canal' : `Editar ${form.name}`}
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={onSubmit} disabled={busy || !canSubmit(form)}>
            {busy ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Nombre" value={form.name} onChange={(event) => patch({ name: event.target.value })} />
          <Select
            label="Tipo"
            value={form.kind}
            disabled={!isNew}
            onChange={(event) => patch({ kind: event.target.value as NotificationChannelKind })}
          >
            <option value="slack">Slack</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Correo</option>
            <option value="discord">Discord</option>
          </Select>
        </div>

        <Select
          label="Gravedad mínima"
          value={form.minSeverity}
          onChange={(event) => patch({ minSeverity: event.target.value as AlertSeverity })}
        >
          <option value="info">info</option>
          <option value="warning">warning</option>
          <option value="critical">critical</option>
        </Select>

        {form.kind === 'slack' && (
          <Field
            label="Canal (solo para mostrar)"
            value={form.slackChannel}
            placeholder="soporte"
            hint="No decide el destino: eso lo hace el webhook. Es solo lo que se ve en esta lista."
            onChange={(event) => patch({ slackChannel: event.target.value })}
          />
        )}

        {form.kind === 'discord' && (
          <Field
            label="Etiqueta (solo para mostrar)"
            value={form.discordLabel}
            placeholder="alertas-occ"
            hint="No decide el destino: eso lo hace el webhook."
            onChange={(event) => patch({ discordLabel: event.target.value })}
          />
        )}

        {form.kind === 'whatsapp' && (
          <>
            <Field
              label="Destinatarios (E.164, sin +)"
              value={form.whatsappRecipients}
              placeholder="5215512345678, 5215587654321"
              onChange={(event) => patch({ whatsappRecipients: event.target.value })}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Nombre de la plantilla"
                value={form.whatsappTemplateName}
                hint="Fuera de la ventana de 24 h, Meta solo entrega plantillas aprobadas."
                onChange={(event) => patch({ whatsappTemplateName: event.target.value })}
              />
              <Field
                label="Idioma de la plantilla"
                value={form.whatsappTemplateLanguage}
                onChange={(event) => patch({ whatsappTemplateLanguage: event.target.value })}
              />
            </div>
            <Field
              label="Identificador del número emisor (opcional)"
              value={form.whatsappPhoneNumberId}
              onChange={(event) => patch({ whatsappPhoneNumberId: event.target.value })}
            />
          </>
        )}

        {form.kind === 'email' && (
          <>
            <Field
              label="Destinatarios"
              value={form.emailRecipients}
              placeholder="soporte@cliente.com, oncall@cliente.com"
              onChange={(event) => patch({ emailRecipients: event.target.value })}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Remitente (opcional)"
                value={form.emailFrom}
                hint="Vacío usa el de la instalación."
                onChange={(event) => patch({ emailFrom: event.target.value })}
              />
              <Field
                label="Prefijo del asunto (opcional)"
                value={form.emailSubjectPrefix}
                onChange={(event) => patch({ emailSubjectPrefix: event.target.value })}
              />
            </div>
          </>
        )}

        <Field
          label={form.kind === 'email' ? `${SECRET_LABEL[form.kind]} (opcional)` : SECRET_LABEL[form.kind]}
          type="password"
          value={form.secretValue}
          placeholder={form.hasOwnSecret ? '••••••••  (vacío = no cambiar)' : undefined}
          hint={
            form.kind === 'email'
              ? 'Vacío usa el relay de correo de la instalación.'
              : !isNew
                ? 'Vacío conserva la credencial guardada. Nunca se muestra un valor existente.'
                : undefined
          }
          onChange={(event) => patch({ secretValue: event.target.value })}
        />
      </div>
    </Modal>
  );
}
